import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { tokens } from '../../design-system/tokens';
import type { DocsArea } from '../console/help/docsBaseUrl';
import { catalogEntry } from './docCatalog';
import { getDocMarkdown } from './docBundle';
import { markdownToSafeHtml, rewriteDocMarkdownLinks } from './markdownRender';

function isDocsArea(s: string | undefined): s is DocsArea {
  return s === 'consola' || s === 'super-admin';
}

type Props = { variant: 'public' | 'console' };

export function DocsArticle({ variant }: Props) {
  const { area, slug } = useParams<{ area: string; slug: string }>();
  const base = variant === 'console' ? '/app/docs' : '/docs';
  const mode = variant === 'console' ? 'app' : 'public';

  const rel = isDocsArea(area) && slug ? `${area}/${slug}` : null;
  const rawMd = rel ? getDocMarkdown(rel) : undefined;
  const meta = isDocsArea(area) && slug ? catalogEntry(area, slug) : undefined;

  const html = useMemo(() => {
    if (!rawMd || !isDocsArea(area)) return '';
    const linked = rewriteDocMarkdownLinks(rawMd, area, mode);
    return markdownToSafeHtml(linked);
  }, [rawMd, area, mode]);

  useEffect(() => {
    const title = meta ? `${meta.label} · Ayuda` : 'Documentación';
    document.title = title;
  }, [meta]);

  if (!isDocsArea(area) || !slug) {
    return (
      <p style={{ color: tokens.colors.error.dark }}>
        Ruta de documentación no válida. <Link to={base}>Volver al índice</Link>
      </p>
    );
  }

  if (!rawMd) {
    return (
      <div>
        <h1 style={{ color: tokens.semantic.textPrimary }}>No encontrado</h1>
        <p style={{ color: tokens.semantic.textSecondary }}>
          No hay contenido para <code>{area}/{slug}</code>. <Link to={base}>Índice</Link>
        </p>
      </div>
    );
  }

  return (
    <article>
      <nav style={{ fontSize: 13, marginBottom: 16 }}>
        <Link to={base} style={{ color: tokens.colors.accent[600], fontWeight: 600, textDecoration: 'none' }}>
          Índice
        </Link>
        <span style={{ color: tokens.semantic.textMuted }}> / </span>
        <span style={{ color: tokens.semantic.textSecondary }}>{meta?.section ?? area}</span>
        <span style={{ color: tokens.semantic.textMuted }}> / </span>
        <span style={{ color: tokens.semantic.textPrimary, fontWeight: 600 }}>{meta?.label ?? slug}</span>
      </nav>
      <div className="va-doc-article" dangerouslySetInnerHTML={{ __html: html }} />
    </article>
  );
}
